import { ID, overdueSectionID, Task, Timestamp } from '@griply/types';
import {
  getDateFilterEndDate,
  getDocumentDurationFilters,
  getOverdueTasksFromTree,
  onUpcomingDeadlineReorder,
} from '@griply/utils';
import { useSortedSmartLists } from 'features/smart-lists';
import { useSortedTags } from 'features/tags';
import { useUpdateTasks } from 'features/tasks';
import React, { useCallback, useMemo } from 'react';
import { CalendarSlotContextMenu } from 'shared/components/connected/calendar-slot-context-menu';
import {
  ForeignTreeItem,
  rootId,
  TaskListItemTypes,
  TreeItem,
  useTreeValue,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import {
  CalendarSlotMenuAnchor,
  useCalendarSlotMenu,
} from 'shared/hooks/use-calendar-slot-menu';
import { useShouldHaveRestOfMonth } from 'shared/hooks/use-should-have-rest-of-month';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';

import { useGoalDeadlineRows } from '../../hooks/use-goal-deadline-rows';
import { findDraggableTasks } from '../../hooks/use-register-draggable-tasks';
import {
  upcomingSectionPrefixes,
  useUpcomingDeadlineTree,
} from '../../hooks/use-upcoming-deadline-tree';
import { GroupedListProps } from '../../types';
import { onUpcomingDeadlineForeignDrop } from './on-upcoming-deadline-foreign-drop';
import * as Styled from './upcoming-deadline-grouping-tasks-list.style';
import {
  UpcomingDeadlineRenderData,
  UpcomingDeadlineRenderDataContext,
} from './upcoming-deadline-render-context';
import { UpcomingDeadlineRenderItem } from './upcoming-deadline-render-item';

const taskItemTypes = [TaskListItemTypes.Task];

const isForeignDropSection = (item: TreeItem) =>
  item.type === TaskListItemTypes.Date && item.id !== overdueSectionID;

export const UpcomingDeadlineGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  goals,
  lifeAreas,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
  denseFormat,
  groupSettings,
  filterDocument,
  isContextMenuOpen,
  defaultNewTaskDate,
}) => {
  const today = useToday();
  const { submit: updateTasks } = useUpdateTasks();
  const { shouldHaveRestOfMonth, restOfMonthStartDate } =
    useShouldHaveRestOfMonth();

  const dateFilterEndDate = useMemo(
    () => getDateFilterEndDate(filterDocument),
    [filterDocument],
  );

  const showCompleted = !!filterDocument?.showCompleted;

  const durationFilters = useMemo(
    () => getDocumentDurationFilters(filterDocument),
    [filterDocument],
  );

  const { deadlineGoals, goalRows: goalRenderData } = useGoalDeadlineRows({
    goals,
    lifeAreas,
    filterDocument,
    showCompleted,
    enabled: groupSettings?.showGoalDeadlines,
  });

  const treeData = useUpcomingDeadlineTree({
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    dateFilterEndDate,
    hideNewTaskRow: groupSettings?.hideNewTaskRow,
    shouldHaveRestOfMonth,
    restOfMonthStartDate,
    showCompleted,
    durationFilters,
    deadlineGoals,
  });

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const _onReorder = useCallback(
    (updatedItems: TreeItem[]) => {
      if (!onReorder) {
        return;
      }

      const result = onUpcomingDeadlineReorder({
        updatedItems,
        tasksMap,
        today,
        weekStartsOn,
        prefixes: upcomingSectionPrefixes,
      });

      onReorder(result.updatedSections, result.updatedTasks);
    },
    [onReorder, tasksMap, today, weekStartsOn],
  );

  const _onForeignDrop = useCallback(
    (droppedItems: ForeignTreeItem[], section: TreeItem) => {
      if (!onReorder) {
        return;
      }

      const result = onUpcomingDeadlineForeignDrop({
        tasks: findDraggableTasks(droppedItems.map(({ sourceId }) => sourceId)),
        section,
        today,
        weekStartsOn,
        prefixes: upcomingSectionPrefixes,
      });
      if (!result) {
        return;
      }

      onReorder(result.updatedSections, result.updatedTasks);
    },
    [onReorder, today, weekStartsOn],
  );

  const displayDue = groupSettings?.displayDue;
  const externalEvents = groupSettings?.externalEvents;
  const timeFormat = groupSettings?.timeFormat;

  const overdueTasks = useMemo(
    () => getOverdueTasksFromTree(treeData, tasksMap),
    [treeData, tasksMap],
  );

  const tags = useSortedTags();
  const smartLists = useSortedSmartLists();
  const { openSlotMenu, slotContextMenu } = useCalendarSlotMenu(
    lifeAreas,
    goals,
    tags,
    smartLists,
  );
  const onAddForDate = useCallback(
    (date: Timestamp, anchor: CalendarSlotMenuAnchor) =>
      openSlotMenu(date, null, anchor),
    [openSlotMenu],
  );

  const renderData = useMemo<UpcomingDeadlineRenderData>(
    () => ({
      tasksMap,
      tasks,
      overdueTasks,
      externalEvents,
      today,
      denseFormat,
      displayDue,
      timeFormat,
      showCompleted,
      goalRenderData,
      onAddForDate,
    }),
    [
      tasksMap,
      tasks,
      overdueTasks,
      externalEvents,
      today,
      denseFormat,
      displayDue,
      timeFormat,
      showCompleted,
      goalRenderData,
      onAddForDate,
    ],
  );

  const tree = useTreeValue();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <UpcomingDeadlineRenderDataContext.Provider value={renderData}>
        <VirtualizedTreeList
          rootItemId={rootId}
          items={treeData}
          renderItem={UpcomingDeadlineRenderItem}
          scrollElement={scrollElement}
          multiselectTypes={taskItemTypes}
          dragTypes={taskItemTypes}
          onReorder={_onReorder}
          onForeignDrop={_onForeignDrop}
          canDropForeignOn={isForeignDropSection}
          collapsedItems={collapsedSections}
          onCollapse={onSectionCollapse}
          denseFormat={denseFormat}
          isContextMenuOpen={isContextMenuOpen}
        />
      </UpcomingDeadlineRenderDataContext.Provider>
      {slotContextMenu.slot && <CalendarSlotContextMenu {...slotContextMenu} />}
    </Styled.Container>
  );
};
